/* app.js — /subtitle-editor: the page.
 *
 * Everything with meaning lives in model.js (pure, tested in Node) and in
 * public/js/subtitle-convert/ (the format engine this tool shares with
 * /subtitle-converter). This file is the DOM: what is on screen, what a click
 * does, and which shared primitive owns each job.
 *
 * ── What the rebuild of 2026-09-22 had to fix ──────────────────────────────
 * The previous version read files with `FileReader.readAsText()`, which means
 * "assume UTF-8". Most Russian .srt files in circulation are windows-1251, so
 * on a Russian-language site the commonest possible file came out as a wall of
 * replacement characters — silently, under a success message. That is the same
 * defect /subtitle-converter was rebuilt to remove, and the decoder that fixes
 * it was already in this repository, unused by this tool.
 *
 * Alongside it: two formats where the engine offers fifteen, a video that could
 * be loaded but never showed a subtitle on it, no waveform, no reading-speed
 * check, no daily cap, a hand-rolled `<a download>` instead of __downloadFile,
 * and a table whose height alone (520 px) put the export button off-screen.
 *
 * ── Ownership map ──────────────────────────────────────────────────────────
 *   decode / parse / write / repair / quality  → subtitle-convert (16 formats)
 *   document edits + undo                      → ./model.js
 *   waveform strip and its gestures            → ./timeline.js
 *   subtitles drawn on the frame               → window.SubtitleOverlay
 *   the video element and its controls         → window.__createTimbricaPlayer
 *   the envelope of a 3-hour file              → window.AudioPeaksStream
 *   saving a file                              → window.__downloadFile
 *   naming it                                  → window.__outputName
 *   the daily cap                              → window.__toolLimit
 */

import {
    load as loadDoc, loadBytes, reload as reloadDoc, convert, outputName,
    writableFormats, readableFormats, writableEncodings, ENCODING_CHOICES,
    analyse, PRESETS, FPS_PRESETS
} from "../subtitle-convert/index.js";
import * as M from "./model.js";
import { createTimeline } from "./timeline.js";

const SLUG = "subtitle-editor";
const $ = (id) => document.getElementById(id);
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
const ae = (name, data) => { try { window.__ae && window.__ae(name, Object.assign({ slug: SLUG }, data || {})); } catch (_) {} };
const stage = (n) => { try { window.__stage && window.__stage(n); } catch (_) {} };

/* Strings come from a <script type="application/json"> block the blade writes,
 * so no translated text is duplicated in JavaScript. */
let T = {};
try { T = JSON.parse(($("seStrings") || {}).textContent || "{}"); } catch (_) {}
const t = (k, vars) => {
    let s = T[k] == null ? k : String(T[k]);
    if (vars) for (const n in vars) s = s.split(":" + n).join(String(vars[n]));
    return s;
};

/* ── state ─────────────────────────────────────────────────────────────── */

const S = {
    doc: null,                 /* the loaded document from subtitle-convert */
    cues: [],
    hist: null,
    sel: new Set(),
    anchor: -1,                /* for shift-click ranges */
    focus: -1,                 /* the cue the inspector is showing */
    limitKey: null,            /* stamped ONCE at load — see the note at stampKey() */
    fileName: "subtitles.srt",
    target: "srt",
    outEncoding: "utf-8",
    fps: 25,
    preset: "netflix",
    quality: null,
    dirty: false,
    media: { url: null, file: null, player: null, overlay: null, duration: 0 },
    tl: null,
    follow: true,
    lastFind: { q: "", i: 0 }
};

/* ── boot ──────────────────────────────────────────────────────────────── */

const el = {};
function grab() {
    for (const id of [
        "seApp", "seEmpty", "seWork", "seUpload", "sePaste", "seBlank", "sePasteBox", "sePasteText",
        "sePasteOk", "sePasteCancel", "seFileName", "seFileMeta", "seReplace", "seClose",
        "seUndo", "seRedo", "seAdd", "seSplit", "seMerge", "seDelete", "seFindBtn", "seFind",
        "seFindIn", "seReplIn", "seFindCase", "seFindWhole", "seFindPrev", "seFindNext",
        "seReplOne", "seReplAll", "seFindCount", "seFindClose",
        "seList", "seListInner", "seCount", "seEncNote", "seEncFix", "seEncKeep", "seEncSel",
        "seInsEmpty", "seInsBody", "seInsNum", "seInsStart", "seInsEnd", "seInsDur", "seInsText",
        "seInsCps", "seInsChars", "seInsPrev", "seInsNext", "seInsIn", "seInsOut", "seInsWrap",
        "seVideoWrap", "seVideoSlot", "seLoadMedia", "seMediaInput", "seMediaBar", "seMediaName",
        "seMediaDrop", "seTlPanel", "seTl", "seTlZoomIn", "seTlZoomOut", "seTlAll", "seTlFollow",
        "seTlState", "seQualList", "seQualSum", "seQualPreset", "seFpsSel", "seQualFix",
        "seShiftMs", "seShiftApply", "seShiftBack", "seSyncA1", "seSyncA2", "seSyncB1", "seSyncB2",
        "seSyncApply", "seSepApply", "seWrapApply", "seWrapChars", "seFormat", "seEncOut",
        "seTools", "seToolsBtn",
        "seDownload", "seDownloadName", "seRestore", "seRestoreYes", "seRestoreNo", "seSnap"
    ]) el[id] = $(id);
}

function boot() {
    grab();
    if (!el.seApp) return;
    fillFormats();
    fillEncodings();
    fillPresets();
    wireEmpty();
    wireToolbar();
    wireInspector();
    wireFind();
    wireTiming();
    wireMedia();
    wireExport();
    wireKeys();
    offerRestore();
    stage("ready");
}

/* The boot call lives at the very BOTTOM of this file, not here.
 * `type=module` is deferred, so `readyState` is already "interactive" by the
 * time this runs and boot() would fire immediately — reaching the `const`
 * lookup tables declared further down before they are initialised. Function
 * declarations hoist; `const` does not, and the whole page dies with
 * "Cannot access 'encLabel' before initialization". */

/* ── select pickers built from the registry, never hand-written ────────── */

function fillFormats() {
    if (!el.seFormat) return;
    for (const f of writableFormats()) {
        const o = document.createElement("option");
        o.value = f.id;
        o.textContent = f.label + " · " + f.name;
        el.seFormat.appendChild(o);
    }
    el.seFormat.value = "srt";
}

const ENC_LABEL = {
    "utf-8": "UTF-8", "utf-8-bom": "UTF-8 + BOM", "windows-1251": "Windows-1251 (Cyrillic)",
    "windows-1252": "Windows-1252 (Western)", "windows-1250": "Windows-1250 (Central Eur.)",
    "windows-1254": "Windows-1254 (Turkish)", "windows-1257": "Windows-1257 (Baltic)",
    "windows-1253": "Windows-1253 (Greek)", "koi8-r": "KOI8-R", "koi8-u": "KOI8-U",
    "iso-8859-2": "ISO-8859-2", "iso-8859-5": "ISO-8859-5", "big5": "Big5",
    "gbk": "GBK", "shift_jis": "Shift_JIS", "euc-kr": "EUC-KR",
    "utf-16le": "UTF-16 LE", "utf-16be": "UTF-16 BE"
};
const encLabel = (id) => ENC_LABEL[id] || id.toUpperCase();

function fillEncodings() {
    /* Two different lists on purpose: everything the decoder can READ goes in
     * the "the file is actually in…" picker, and only what the encoder can
     * WRITE goes in the export picker. Offering Shift_JIS as an output when
     * nothing can produce it would be a control that fails after the click. */
    if (el.seEncOut) {
        for (const id of writableEncodings()) {
            const o = document.createElement("option");
            o.value = id; o.textContent = encLabel(id);
            el.seEncOut.appendChild(o);
        }
        el.seEncOut.value = "utf-8";
    }
    if (el.seEncSel) {
        for (const id of ENCODING_CHOICES) {
            const o = document.createElement("option");
            o.value = id; o.textContent = encLabel(id);
            el.seEncSel.appendChild(o);
        }
    }
    if (el.seFpsSel) {
        for (const f of FPS_PRESETS) {
            const o = document.createElement("option");
            o.value = String(f.value);
            o.textContent = String(f.value) + " fps";
            el.seFpsSel.appendChild(o);
        }
        el.seFpsSel.value = "25";
    }
}

function fillPresets() {
    if (!el.seQualPreset) return;
    for (const id of Object.keys(PRESETS)) {
        const o = document.createElement("option");
        o.value = id;
        o.textContent = t("preset_" + id);
        el.seQualPreset.appendChild(o);
    }
    el.seQualPreset.value = "netflix";
}

/* ── loading ───────────────────────────────────────────────────────────── */

function wireEmpty() {
    if (el.seUpload && window.__bindUploaderFile) {
        /* ⚠️ Never a bare addEventListener here: this module is
         * `type=module`, so it runs AFTER the document is parsed, and on a slow
         * connection the user has already picked a file by then. The helper
         * replays that pick; a raw listener drops it and the tool sits dead. */
        window.__bindUploaderFile(el.seUpload, (f) => { if (f) openFile(f); });
    } else if (el.seUpload) {
        on(el.seUpload, "tool-uploader:file", (e) => {
            const f = e.detail && e.detail.file; if (f) openFile(f);
        });
    }
    on(el.seUpload, "tool-uploader:clear", () => closeDoc());

    on(el.sePaste, "click", async () => {
        show(el.sePasteBox, true);
        el.sePasteText && el.sePasteText.focus({ preventScroll: true });
        try {
            if (navigator.clipboard && navigator.clipboard.readText) {
                const txt = await navigator.clipboard.readText();
                if (txt && txt.length > 10 && el.sePasteText && !el.sePasteText.value) el.sePasteText.value = txt;
            }
        } catch (_) { /* denied — the textarea is still there */ }
    });
    on(el.sePasteCancel, "click", () => show(el.sePasteBox, false));
    on(el.sePasteOk, "click", () => {
        const txt = (el.sePasteText && el.sePasteText.value) || "";
        if (!txt.trim()) return;
        openBytes(new TextEncoder().encode(txt), "pasted.srt", "paste");
        show(el.sePasteBox, false);
    });
    on(el.seBlank, "click", () => {
        const cues = [M.cue(0, 2000, "")];
        adopt({ name: "subtitles.srt", cues, fps: 25, meta: {}, encoding: "utf-8", format: "srt" }, cues, "blank");
        focusCue(0, true);
    });
    on(el.seReplace, "click", () => {
        const input = document.querySelector('[data-uploader-for="seUpload"]');
        if (input) input.click();
    });
    on(el.seClose, "click", () => closeDoc());
}

async function openFile(file) {
    try {
        const doc = await loadDoc(file, {});
        finishOpen(doc, file, "pick");
    } catch (e) {
        window.__handleError ? window.__handleError(e, SLUG, { say: t("err_read") }) : console.error(e);
    }
}

function openBytes(bytes, name, source) {
    try { finishOpen(loadBytes(bytes, name, {}), { name, size: bytes.length, lastModified: Date.now() }, source); }
    catch (e) { window.__toast && window.__toast(t("err_read"), "error"); }
}

function finishOpen(doc, file, source) {
    if (doc.error === "unknown_format" || doc.error === "no_timings") {
        window.__toast && window.__toast(t("err_format"), "error");
        ae("feature_use", { f: "open_refused", why: doc.error });
        return;
    }
    if (!doc.cues.length) {
        window.__toast && window.__toast(t("err_empty"), "error");
        ae("feature_use", { f: "open_refused", why: "no_cues" });
        return;
    }
    stampKey(file);
    adopt(doc, M.normalise(doc.cues), source);
}

/**
 * The cap key is stamped ONCE, when the file arrives.
 *
 * ⚠️ Deriving it later from the live File is the documented trap: the parts of
 * the key (name|size|lastModified) change the moment a tool hands its own
 * output back into itself, so "two files a day" quietly became "two saves a
 * day" on a tool whose own wall promises that re-editing the same file is free.
 * Here the same file can be exported to five formats after an hour of edits and
 * still costs one unit — which is what the wall says.
 */
function stampKey(file) {
    S.limitKey = file ? [file.name, file.size, file.lastModified || 0].join("|") : null;
}

function adopt(doc, cues, source) {
    S.doc = doc;
    S.cues = cues;
    S.hist = M.createHistory(cues);
    S.sel = new Set(); S.anchor = -1; S.focus = -1;
    S.fileName = doc.name || "subtitles.srt";
    S.fps = doc.fps || 25;
    S.dirty = false;
    syncGuard();
    if (el.seFpsSel) el.seFpsSel.value = String(S.fps);

    /* Open in the format the file came in — the commonest intent is "fix this
     * file", not "convert it". Converting is one select away either way. */
    const readable = readableFormats();
    const w = writableFormats().some((f) => f.id === doc.format);
    S.target = w ? doc.format : "srt";
    if (el.seFormat) el.seFormat.value = S.target;

    swapView(true);
    showEncodingNote(doc);
    refreshAll();
    focusCue(0, false);
    ae("tool_start", { src: source, fmt: doc.format, cues: cues.length, enc: doc.encoding,
        moji: (doc.mojibake && doc.mojibake.found) ? 1 : 0, formats_known: readable.length });
    stage("loaded");
}

function closeDoc() {
    if (S.media.player) stopMedia();
    S.doc = null; S.cues = []; S.hist = null; S.sel.clear(); S.focus = -1;
    S.dirty = false;
    syncGuard();
    clearSave();
    swapView(false);
}

function swapView(work) {
    const apply = () => {
        show(el.seEmpty, !work);
        show(el.seWork, work);
    };
    /* The shared wrapper owns every reason a transition must not run (reduced
     * motion, hidden tab, a transition already in flight). Calling
     * startViewTransition directly is what produced unhandled rejections
     * elsewhere on the site. */
    if (window.__viewTransition) window.__viewTransition(apply); else apply();
}

function show(node, yes) { if (node) node.hidden = !yes; }

/* ── encoding: the defect this rebuild exists for ──────────────────────── */

function showEncodingNote(doc) {
    if (!el.seEncNote) return;
    /* ⚠️ `doc.mojibake` is a VERDICT OBJECT — {found, preview, repaired} — not a
     * boolean. Treating it as one raises the warning on every clean file, which
     * is how the first build of this page told a perfectly decoded UTF-8 script
     * that its encoding was doubtful and spent 103 px of the first screen
     * saying so. */
    const moji = !!(doc.mojibake && doc.mojibake.found);
    const shaky = moji || (doc.encodingConfidence != null && doc.encodingConfidence < 0.55);
    if (el.seEncSel) el.seEncSel.value = doc.encoding || "utf-8";
    show(el.seEncFix, moji);
    if (!shaky) { show(el.seEncNote, false); return; }
    /* For double-encoded text the useful sample is what the FIX would produce —
     * the broken version is already visible in the list behind the notice. */
    el.seEncNote.querySelector("[data-enc-sample]").textContent =
        (moji ? doc.mojibake.preview : (doc.cues[0] && doc.cues[0].text) || "").slice(0, 70);
    el.seEncNote.querySelector("[data-enc-title]").textContent =
        moji ? t("enc_moji_title") : t("enc_guess_title");
    show(el.seEncNote, true);
    ae("feature_use", { f: "encoding_note", moji: moji ? 1 : 0, enc: doc.encoding,
        conf: Math.round((doc.encodingConfidence || 0) * 100) });
}

function reopenWith(opts) {
    if (!S.doc || !S.doc.bytes) return;
    const next = reloadDoc(S.doc, opts);
    if (!next.cues.length) { window.__toast && window.__toast(t("err_empty"), "error"); return; }
    S.doc = next;
    pushCues(M.normalise(next.cues), null);
    showEncodingNote(next);
    refreshAll();
}

/* ── the document, edited ──────────────────────────────────────────────── */

function pushCues(next, tag) {
    if (next === S.cues) return;
    S.cues = next;
    S.hist.push(next, tag);
    S.dirty = true;
    syncGuard();
    /* Chrome is refreshed HERE, not by each caller. Leaving it to the callers
     * is how the first build shipped an Undo button that stayed greyed out
     * after typing: the text handler repainted the row, the meters and the
     * quality pass, and forgot the one control whose whole job is to say that
     * there is now something to undo. */
    updateChrome();
    scheduleSave();
}

function refreshAll() {
    renderList();
    renderInspector();
    runQuality();
    if (S.tl) { S.tl.setCues(S.cues); S.tl.setSelection(S.sel); S.tl.setDuration(Math.max(S.media.duration, M.totalDuration(S.cues))); }
    if (S.media.overlay) S.media.overlay.loadCues(overlayCues());
    updateChrome();
}

function updateChrome() {
    if (el.seCount) el.seCount.textContent = t("count_cues", { n: S.cues.length });
    if (el.seFileName) el.seFileName.textContent = S.fileName;
    if (el.seFileMeta) {
        const dur = M.totalDuration(S.cues);
        el.seFileMeta.textContent = [
            (S.doc && S.doc.format ? S.doc.format.toUpperCase() : ""),
            (S.doc && S.doc.encoding ? S.doc.encoding.toUpperCase() : ""),
            dur ? M.msToClock(dur).slice(0, 8) : ""
        ].filter(Boolean).join(" · ");
    }
    if (el.seUndo) el.seUndo.disabled = !S.hist || !S.hist.canUndo;
    if (el.seRedo) el.seRedo.disabled = !S.hist || !S.hist.canRedo;
    if (el.seDelete) el.seDelete.disabled = S.sel.size === 0;
    if (el.seMerge) el.seMerge.disabled = S.sel.size < 2;
    if (el.seSplit) el.seSplit.disabled = S.focus < 0;
    if (el.seDownload) el.seDownload.disabled = !S.cues.length;
    if (el.seDownloadName) el.seDownloadName.textContent = exportName();
}

/* ── the cue list (windowed) ───────────────────────────────────────────── */

let rowH = 56;
let listFrom = 0, listTo = 0;

function readRowH() {
    const v = parseFloat(getComputedStyle(el.seApp).getPropertyValue("--se-row"));
    if (v > 20) rowH = v;
}

/** Only the rows in view exist in the DOM. A feature film is 1 500–2 500 cues;
 *  rendering them all costs seconds on a phone and megabytes of DOM, and the
 *  user can see fourteen of them. */
function renderList() {
    if (!el.seList || !el.seListInner) return;
    readRowH();
    el.seListInner.style.height = (S.cues.length * rowH) + "px";
    paintRows(true);
}

function paintRows(force) {
    const view = el.seList;
    if (!view) return;
    const top = view.scrollTop;
    const h = view.clientHeight || 400;
    const from = Math.max(0, Math.floor(top / rowH) - 4);
    const to = Math.min(S.cues.length, Math.ceil((top + h) / rowH) + 4);
    if (!force && from === listFrom && to === listTo) return;
    listFrom = from; listTo = to;

    const frag = document.createDocumentFragment();
    for (let i = from; i < to; i++) frag.appendChild(rowNode(i));
    el.seListInner.textContent = "";
    el.seListInner.appendChild(frag);
}

function rowNode(i) {
    const c = S.cues[i];
    const row = document.createElement("div");
    row.className = "se-row" + (S.sel.has(i) ? " sel" : "") + (i === S.focus ? " focus" : "");
    row.style.top = (i * rowH) + "px";
    row.dataset.i = String(i);
    row.setAttribute("role", "option");
    row.setAttribute("aria-selected", S.sel.has(i) ? "true" : "false");

    const q = S.quality && S.quality.byIndex ? S.quality.byIndex[i] : null;
    if (q) row.classList.add(q.level === "bad" ? "q-bad" : "q-warn");

    const n = document.createElement("span");
    n.className = "se-row-n"; n.textContent = String(i + 1);

    const time = document.createElement("span");
    time.className = "se-row-t";
    time.textContent = M.msToClock(c.start).slice(0, 8) + " → " + M.msToClock(c.end).slice(3, 8);

    const dur = document.createElement("span");
    dur.className = "se-row-d";
    dur.textContent = M.msToShort(c.end - c.start);

    const txt = document.createElement("span");
    txt.className = "se-row-x";
    txt.textContent = (c.text || "").replace(/<[^>]*>/g, "") || t("row_empty");
    if (!c.text) txt.classList.add("muted");

    const cps = document.createElement("span");
    cps.className = "se-row-c";
    const v = cpsOf(c);
    cps.textContent = v == null ? "" : v.toFixed(0);
    if (q && q.cps) cps.classList.add(q.level === "bad" ? "bad" : "warn");

    row.append(n, time, dur, txt, cps);
    return row;
}

function cpsOf(c) {
    const dur = (c.end - c.start) / 1000;
    if (dur <= 0) return null;
    const chars = (c.text || "").replace(/<[^>]*>/g, "").replace(/\n/g, "").length;
    return chars / dur;
}

/** Bring a row into the LIST's own scroll box. Never the page's — an editor
 *  that scrolls the document under you while you arrow through cues is the
 *  behaviour this codebase forbids outright. */
function revealRow(i) {
    const view = el.seList;
    if (!view || i < 0) return;
    const top = i * rowH, bottom = top + rowH;
    if (top < view.scrollTop) view.scrollTop = top - rowH;
    else if (bottom > view.scrollTop + view.clientHeight) view.scrollTop = bottom - view.clientHeight + rowH;
}

/* ── selection ─────────────────────────────────────────────────────────── */

function selectRow(i, additive, range) {
    if (i < 0 || i >= S.cues.length) return;
    if (additive) {
        if (S.sel.has(i)) S.sel.delete(i); else S.sel.add(i);
        S.anchor = i;
    } else if (range && S.anchor >= 0) {
        S.sel.clear();
        const a = Math.min(S.anchor, i), b = Math.max(S.anchor, i);
        for (let k = a; k <= b; k++) S.sel.add(k);
    } else {
        S.sel.clear(); S.sel.add(i); S.anchor = i;
    }
    S.focus = i;
    S.hist && S.hist.seal();
    paintRows(true);
    renderInspector();
    if (S.tl) { S.tl.setSelection(S.sel); S.tl.focusCue(S.cues[i]); }
    updateChrome();
}

function focusCue(i, alsoSeek) {
    selectRow(i, false, false);
    revealRow(i);
    if (alsoSeek && S.media.player && S.cues[i]) S.media.player.seek(S.cues[i].start / 1000);
}

/* ── inspector ─────────────────────────────────────────────────────────── */

function renderInspector() {
    const has = S.focus >= 0 && S.focus < S.cues.length;
    show(el.seInsEmpty, !has);
    show(el.seInsBody, has);
    if (!has) return;
    const c = S.cues[S.focus];
    el.seInsNum.textContent = "#" + (S.focus + 1);
    if (document.activeElement !== el.seInsStart) el.seInsStart.value = M.msToClock(c.start);
    if (document.activeElement !== el.seInsEnd) el.seInsEnd.value = M.msToClock(c.end);
    el.seInsDur.textContent = ((c.end - c.start) / 1000).toFixed(2) + " " + t("unit_s");
    if (document.activeElement !== el.seInsText) el.seInsText.value = c.text;
    paintCueMeters(c);
}

function paintCueMeters(c) {
    const limits = (S.quality && S.quality.limits) || { cps: 17, chars: 42, lines: 2 };
    const v = cpsOf(c);
    el.seInsCps.textContent = v == null ? "—" : v.toFixed(1);
    el.seInsCps.className = "se-meter " + (v == null ? "" : v > limits.cps * 1.25 ? "bad" : v > limits.cps ? "warn" : "good");
    const lines = (c.text || "").split("\n");
    const longest = lines.reduce((m, l) => Math.max(m, l.replace(/<[^>]*>/g, "").length), 0);
    el.seInsChars.textContent = longest + "/" + limits.chars + " · " + lines.length + t("unit_lines");
    el.seInsChars.className = "se-meter " + (longest > limits.chars || lines.length > limits.lines ? "warn" : "good");
}

function wireInspector() {
    on(el.seInsText, "input", () => {
        if (S.focus < 0) return;
        pushCues(M.setText(S.cues, S.focus, el.seInsText.value), "text:" + S.focus);
        paintCueMeters(S.cues[S.focus]);
        paintRows(true);
        if (S.media.overlay) S.media.overlay.loadCues(overlayCues());
        scheduleQuality();
    });
    on(el.seInsText, "blur", () => { S.hist && S.hist.seal(); updateChrome(); });

    for (const [node, field] of [[el.seInsStart, "start"], [el.seInsEnd, "end"]]) {
        on(node, "change", () => {
            if (S.focus < 0) return;
            const ms = M.clockToMs(node.value);
            if (ms == null) {
                /* A refused time keeps the old value and says so. The previous
                 * version answered 0 and moved the line to the start of the film. */
                node.classList.add("bad");
                node.value = M.msToClock(S.cues[S.focus][field]);
                setTimeout(() => node.classList.remove("bad"), 1200);
                ae("feature_use", { f: "time_refused" });
                return;
            }
            node.classList.remove("bad");
            applyEdge(S.focus, field, ms, el.seSnap && el.seSnap.checked);
        });
    }

    on(el.seInsPrev, "click", () => focusCue(Math.max(0, S.focus - 1), true));
    on(el.seInsNext, "click", () => focusCue(Math.min(S.cues.length - 1, S.focus + 1), true));
    on(el.seInsIn, "click", () => setEdgeAtPlayhead("start"));
    on(el.seInsOut, "click", () => setEdgeAtPlayhead("end"));
    on(el.seInsWrap, "click", () => {
        if (S.focus < 0) return;
        const limits = (S.quality && S.quality.limits) || { chars: 42, lines: 2 };
        pushCues(M.setText(S.cues, S.focus, M.wrapText(S.cues[S.focus].text, limits.chars, limits.lines)), null);
        renderInspector(); paintRows(true); scheduleQuality();
        ae("feature_use", { f: "wrap_one" });
    });
}

function applyEdge(idx, field, ms, snap) {
    const v = snap ? M.snapFrame(ms, S.fps) : ms;
    pushCues(M.setEdge(S.cues, idx, field, v), null);
    renderInspector(); paintRows(true);
    if (S.tl) S.tl.setCues(S.cues);
    if (S.media.overlay) S.media.overlay.loadCues(overlayCues());
    scheduleQuality();
    updateChrome();
}

function playheadMs() {
    const p = S.media.player;
    if (!p || !p.video) return null;
    return Math.round(p.video.currentTime * 1000);
}

function setEdgeAtPlayhead(field) {
    const ms = playheadMs();
    if (ms == null) { window.__toast && window.__toast(t("need_media"), "warning"); return; }
    if (S.focus < 0) return;
    applyEdge(S.focus, field, ms, el.seSnap && el.seSnap.checked);
    ae("feature_use", { f: "edge_at_playhead", edge: field });
}

/* ── toolbar ───────────────────────────────────────────────────────────── */

function wireToolbar() {
    on(el.seList, "scroll", () => paintRows(false), { passive: true });
    on(el.seList, "click", (e) => {
        const row = e.target.closest(".se-row");
        if (!row) return;
        const i = +row.dataset.i;
        selectRow(i, e.ctrlKey || e.metaKey, e.shiftKey);
        if (S.media.player && !(e.ctrlKey || e.metaKey || e.shiftKey)) S.media.player.seek(S.cues[i].start / 1000);
    });
    on(el.seList, "dblclick", (e) => {
        const row = e.target.closest(".se-row");
        if (!row) return;
        selectRow(+row.dataset.i, false, false);
        el.seInsText && el.seInsText.focus({ preventScroll: true });
    });

    on(el.seUndo, "click", () => stepHistory(-1));
    on(el.seRedo, "click", () => stepHistory(1));
    on(el.seAdd, "click", () => {
        const at = playheadMs();
        const r = M.insertAfter(S.cues, S.focus, at != null && S.focus < 0 ? { at } : {});
        pushCues(r.cues, null);
        refreshAll(); focusCue(r.index, false);
        el.seInsText && el.seInsText.focus({ preventScroll: true });
        ae("feature_use", { f: "cue_add" });
    });
    on(el.seDelete, "click", () => {
        if (!S.sel.size) return;
        const r = M.removeAt(S.cues, Array.from(S.sel));
        pushCues(r.cues, null);
        S.sel.clear(); if (r.index >= 0) S.sel.add(r.index);
        S.focus = r.index;
        refreshAll(); revealRow(r.index);
        ae("feature_use", { f: "cue_delete", n: 1 });
    });
    on(el.seMerge, "click", () => {
        if (S.sel.size < 2) return;
        const n = S.sel.size;
        const r = M.merge(S.cues, Array.from(S.sel));
        pushCues(r.cues, null);
        S.sel.clear(); S.sel.add(r.index); S.focus = r.index;
        refreshAll(); revealRow(r.index);
        ae("feature_use", { f: "cue_merge", n });
    });
    on(el.seSplit, "click", () => doSplit());
}

function doSplit() {
    if (S.focus < 0) return;
    const opts = {};
    /* A caret inside the text wins: that is the user pointing at the sentence
     * boundary. Otherwise a playhead inside the cue wins. Otherwise the model
     * finds the line break or the middle word. */
    if (document.activeElement === el.seInsText && el.seInsText.selectionStart > 0) {
        opts.caret = el.seInsText.selectionStart;
    } else {
        const ms = playheadMs();
        const c = S.cues[S.focus];
        if (ms != null && ms > c.start && ms < c.end) opts.at = ms;
    }
    const r = M.split(S.cues, S.focus, opts);
    pushCues(r.cues, null);
    S.sel.clear(); S.sel.add(r.index); S.focus = r.index;
    refreshAll(); revealRow(r.index);
    ae("feature_use", { f: "cue_split", by: opts.caret != null ? "caret" : opts.at != null ? "playhead" : "auto" });
}

function stepHistory(dir) {
    if (!S.hist) return;
    const next = dir < 0 ? S.hist.undo() : S.hist.redo();
    if (next === S.cues) return;
    S.cues = next;
    S.dirty = true;
    syncGuard();
    if (S.focus >= S.cues.length) S.focus = S.cues.length - 1;
    S.sel = new Set(Array.from(S.sel).filter((i) => i < S.cues.length));
    refreshAll();
    scheduleSave();
}

/* ── find & replace ────────────────────────────────────────────────────── */

function findOpts() {
    return {
        caseSensitive: !!(el.seFindCase && el.seFindCase.checked),
        whole: !!(el.seFindWhole && el.seFindWhole.checked)
    };
}

function wireFind() {
    const toggle = () => {
        const open = el.seFind.hidden;
        show(el.seFind, open);
        if (open) { el.seFindIn.focus({ preventScroll: true }); runFind(); }
        else clearFindMarks();
    };
    on(el.seFindBtn, "click", toggle);
    on(el.seFindClose, "click", toggle);
    on(el.seFindIn, "input", runFind);
    on(el.seFindCase, "change", runFind);
    on(el.seFindWhole, "change", runFind);
    on(el.seFindIn, "keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); jumpMatch(e.shiftKey ? -1 : 1); } });
    on(el.seFindNext, "click", () => jumpMatch(1));
    on(el.seFindPrev, "click", () => jumpMatch(-1));

    on(el.seReplOne, "click", () => {
        const q = el.seFindIn.value;
        if (!q) return;
        const r = M.replaceOne(S.cues, q, el.seReplIn.value, findOpts(), Math.max(0, S.focus));
        if (!r.done) { window.__toast && window.__toast(t("find_none"), "info"); return; }
        pushCues(r.cues, null);
        S.focus = r.index; S.sel = new Set([r.index]);
        refreshAll(); revealRow(r.index); runFind();
    });
    on(el.seReplAll, "click", () => {
        const q = el.seFindIn.value;
        if (!q) return;
        const r = M.replaceAll(S.cues, q, el.seReplIn.value, findOpts());
        if (!r.hits) { window.__toast && window.__toast(t("find_none"), "info"); return; }
        pushCues(r.cues, null);
        refreshAll(); runFind();
        window.__toast && window.__toast(t("replaced", { n: r.hits, c: r.cuesChanged }), "success");
        ae("feature_use", { f: "replace_all", hits: r.hits });
    });
}

let findHits = [];
function runFind() {
    const q = el.seFindIn ? el.seFindIn.value : "";
    const r = M.findMatches(S.cues, q, findOpts());
    findHits = r.indices;
    if (el.seFindCount) {
        el.seFindCount.textContent = !q ? "" : r.hits ? t("find_hits", { n: r.hits, c: r.indices.length }) : t("find_none");
        el.seFindCount.classList.toggle("bad", !!q && !r.hits);
    }
    markFindRows();
}

function markFindRows() {
    const set = new Set(findHits);
    for (const row of el.seListInner.children) row.classList.toggle("hit", set.has(+row.dataset.i));
}
function clearFindMarks() {
    findHits = [];
    for (const row of el.seListInner.children) row.classList.remove("hit");
}

function jumpMatch(dir) {
    if (!findHits.length) return;
    let k = findHits.findIndex((i) => (dir > 0 ? i > S.focus : i < S.focus));
    if (k < 0) k = dir > 0 ? 0 : findHits.length - 1;
    else if (dir < 0) { for (let j = findHits.length - 1; j >= 0; j--) if (findHits[j] < S.focus) { k = j; break; } }
    focusCue(findHits[k], true);
    markFindRows();
}

/* ── quality ───────────────────────────────────────────────────────────── */

/* The check costs ~99 ms on a feature film (1 800 cues; Intl.Segmenter over
 * every line is most of it), so it must never sit between a keystroke and the
 * letter appearing. Debounced past a normal typing rhythm, and handed to idle
 * time where the browser offers it. The edit itself is 2 ms and stays
 * synchronous — measured, not assumed. */
let qualTimer = 0, qualIdle = 0;
function scheduleQuality() {
    clearTimeout(qualTimer);
    if (qualIdle && window.cancelIdleCallback) { cancelIdleCallback(qualIdle); qualIdle = 0; }
    qualTimer = setTimeout(() => {
        if (window.requestIdleCallback) qualIdle = requestIdleCallback(() => { qualIdle = 0; runQuality(); }, { timeout: 800 });
        else runQuality();
    }, 400);
}

function runQuality() {
    if (!S.cues.length) { S.quality = null; if (el.seQualList) el.seQualList.textContent = ""; return; }
    const preset = (el.seQualPreset && el.seQualPreset.value) || "netflix";
    const res = analyse(S.cues, { preset, fps: S.fps });
    /* analyse() returns per-cue issue records; index them so a row can colour
     * itself in O(1) while the list scrolls. */
    const byIndex = Object.create(null);
    for (const iss of res.issues) {
        const hard = iss.faults.some((f) => f === "overlap" || f === "empty" || f === "tooShort");
        byIndex[iss.index] = {
            level: hard || (iss.cps && iss.cps > res.limits.cps * 1.3) ? "bad" : "warn",
            cps: !!(iss.cps && iss.cps > res.limits.cps),
            faults: iss.faults
        };
    }
    res.byIndex = byIndex;
    S.quality = res;
    paintQuality(res);
    paintRows(true);
    if (S.focus >= 0 && S.cues[S.focus]) paintCueMeters(S.cues[S.focus]);
}

const FAULT_ORDER = ["overlap", "empty", "fast", "tooShort", "tooLong", "longLine", "manyLines", "tightGap"];

function paintQuality(res) {
    if (!el.seQualList) return;
    el.seQualList.textContent = "";
    let total = 0;
    for (const key of FAULT_ORDER) {
        const n = res.counts[key] || 0;
        if (!n) continue;
        total += n;
        const b = document.createElement("button");
        b.type = "button";
        b.className = "se-qchip " + (key === "overlap" || key === "empty" ? "bad" : "warn");
        b.dataset.fault = key;
        b.innerHTML = "";
        const label = document.createElement("span");
        label.textContent = t("fault_" + key);
        const num = document.createElement("b");
        num.textContent = String(n);
        b.append(label, num);
        b.title = t("fault_jump");
        on(b, "click", () => jumpFault(key));
        el.seQualList.appendChild(b);
    }
    if (el.seQualSum) {
        el.seQualSum.textContent = total ? t("qual_issues", { n: total }) : t("qual_clean");
        el.seQualSum.className = "se-qual-sum " + (total ? "warn" : "good");
    }
}

let faultCursor = Object.create(null);
function jumpFault(key) {
    const hits = S.quality.issues.filter((i) => i.faults.indexOf(key) >= 0).map((i) => i.index);
    if (!hits.length) return;
    const k = (faultCursor[key] || 0) % hits.length;
    faultCursor[key] = k + 1;
    focusCue(hits[k], true);
    ae("feature_use", { f: "quality_jump", fault: key });
}

/* ── timing operations ─────────────────────────────────────────────────── */

function wireTiming() {
    on(el.seToolsBtn, "click", () => {
        const open = el.seTools.hidden;
        show(el.seTools, open);
        el.seToolsBtn.setAttribute("aria-expanded", open ? "true" : "false");
        if (open) ae("feature_use", { f: "timing_tools_open" });
    });
    on(el.seQualPreset, "change", () => { runQuality(); ae("feature_use", { f: "preset", v: el.seQualPreset.value }); });
    on(el.seFpsSel, "change", () => { S.fps = parseFloat(el.seFpsSel.value) || 25; runQuality(); });
    on(el.seEncFix, "click", () => { reopenWith({ repairMojibake: true }); ae("feature_use", { f: "moji_fix" }); });
    on(el.seEncKeep, "click", () => show(el.seEncNote, false));
    on(el.seEncSel, "change", () => { reopenWith({ encoding: el.seEncSel.value }); ae("feature_use", { f: "encoding_switch", v: el.seEncSel.value }); });

    const shiftBy = (sign) => {
        const ms = M.clockToMs(el.seShiftMs.value) ?? parseFloat(el.seShiftMs.value) * 1000;
        if (!isFinite(ms) || !ms) return;
        const only = S.sel.size > 1 ? Array.from(S.sel) : null;
        pushCues(M.shift(S.cues, sign * ms, only), null);
        refreshAll();
        window.__toast && window.__toast(t("shift_done", { n: (sign * ms / 1000).toFixed(2) }), "success");
        ae("feature_use", { f: "shift", ms: sign * ms, scope: only ? "sel" : "all" });
    };
    on(el.seShiftApply, "click", () => shiftBy(1));
    on(el.seShiftBack, "click", () => shiftBy(-1));

    on(el.seSyncApply, "click", () => {
        const a1 = M.clockToMs(el.seSyncA1.value), a2 = M.clockToMs(el.seSyncA2.value);
        const b1 = M.clockToMs(el.seSyncB1.value), b2 = M.clockToMs(el.seSyncB2.value);
        if (a1 == null || a2 == null || b1 == null || b2 == null) {
            window.__toast && window.__toast(t("sync_need"), "warning"); return;
        }
        if (b1 === a1) { window.__toast && window.__toast(t("sync_same"), "warning"); return; }
        pushCues(M.twoPointSync(S.cues, a1, a2, b1, b2), null);
        refreshAll();
        ae("feature_use", { f: "two_point_sync" });
        window.__toast && window.__toast(t("sync_done"), "success");
    });

    on(el.seSepApply, "click", () => {
        const gapMs = S.quality ? S.quality.limits.gapMs : Math.round(2000 / S.fps);
        const r = M.separate(S.cues, gapMs);
        if (!r.changed) { window.__toast && window.__toast(t("sep_none"), "info"); return; }
        pushCues(r.cues, null); refreshAll();
        window.__toast && window.__toast(t("sep_done", { n: r.changed }), "success");
        ae("feature_use", { f: "separate", n: r.changed });
    });

    on(el.seWrapApply, "click", () => {
        const limits = (S.quality && S.quality.limits) || { chars: 42, lines: 2 };
        const chars = parseInt(el.seWrapChars.value, 10) || limits.chars;
        pushCues(M.rewrapAll(S.cues, chars, limits.lines), null);
        refreshAll();
        window.__toast && window.__toast(t("wrap_done"), "success");
        ae("feature_use", { f: "rewrap_all", chars });
    });

    on(el.seQualFix, "click", () => {
        /* One press for the three mechanical faults, so the common case is not
         * four visits to a drawer: pull overlaps apart, re-wrap over-long lines,
         * and drop the empties. Reading speed is NOT touched — fixing that
         * means stealing time from a neighbour, and that is a decision. */
        const limits = (S.quality && S.quality.limits) || { chars: 42, lines: 2, gapMs: 80 };
        let next = M.rewrapAll(S.cues, limits.chars, limits.lines);
        const sep = M.separate(next, limits.gapMs);
        next = sep.cues;
        const before = next.length;
        next = next.filter((c) => c.text.trim() !== "");
        if (!next.length) next = sep.cues;
        pushCues(next, null);
        S.sel.clear(); S.focus = Math.min(S.focus, next.length - 1);
        refreshAll();
        window.__toast && window.__toast(t("autofix_done", { n: sep.changed + (before - next.length) }), "success");
        ae("feature_use", { f: "autofix", sep: sep.changed, empty: before - next.length });
    });
}

/* ── media: video, subtitles on the frame, waveform ────────────────────── */

function overlayCues() {
    /* SubtitleOverlay speaks seconds and wants a sanitized `html` field for the
     * markup it is allowed to render. */
    return S.cues.map((c) => ({
        start: c.start / 1000, end: c.end / 1000,
        text: (c.text || "").replace(/<[^>]*>/g, ""),
        html: escapeCue(c.text || "")
    }));
}

function escapeCue(raw) {
    const esc = String(raw).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    /* Put back only the three inline tags the overlay styles. Anything else the
     * file carried stays visible as text rather than being executed. */
    return esc.replace(/&lt;(\/?)(i|b|u)&gt;/gi, "<$1$2>").replace(/\n/g, "<br>");
}

function wireMedia() {
    on(el.seLoadMedia, "click", () => el.seMediaInput && el.seMediaInput.click());
    on(el.seMediaInput, "change", () => {
        const f = el.seMediaInput.files && el.seMediaInput.files[0];
        if (f) openMedia(f);
    });
    on(el.seMediaDrop, "click", () => stopMedia());
    on(el.seTlZoomIn, "click", () => S.tl && S.tl.zoom(1 / 1.6));
    on(el.seTlZoomOut, "click", () => S.tl && S.tl.zoom(1.6));
    on(el.seTlAll, "click", () => S.tl && S.tl.zoomAll());
    on(el.seTlFollow, "change", () => { S.follow = el.seTlFollow.checked; });

    /* The strip exists even without media: the cue blocks alone already show
     * overlaps and rhythm, and a timeline that appears only sometimes is a
     * control people never learn. */
    ensureTimeline();
}

function ensureTimeline() {
    if (S.tl || !el.seTl) return;
    S.tl = createTimeline(el.seTl, {
        onSeek: (ms) => { if (S.media.player) S.media.player.seek(ms / 1000); },
        onSelect: (i, additive, range) => { selectRow(i, additive, range); revealRow(i); },
        onEdit: (i, start, end, phase) => {
            if (phase === "move") {
                /* Live drag: write straight into the array so the paint is
                 * immediate, and only tell history when the pointer lifts. */
                const next = S.cues.slice();
                next[i] = M.patch(S.cues[i], { start, end });
                S.cues = next;
                S.tl.setCues(S.cues);
                renderInspector(); paintRows(true);
            } else {
                const snap = el.seSnap && el.seSnap.checked;
                const c = S.cues[i];
                const next = S.cues.slice();
                next[i] = M.patch(c, snap
                    ? { start: M.snapFrame(c.start, S.fps), end: M.snapFrame(c.end, S.fps) }
                    : {});
                pushCues(next, null);
                refreshAll();
                ae("feature_use", { f: "timeline_drag" });
            }
        }
    });
    S.tl.setCues(S.cues);
    S.tl.setDuration(M.totalDuration(S.cues));
}

async function openMedia(file) {
    stopMedia();
    if (!window.__createTimbricaPlayer) { window.__toast && window.__toast(t("err_media"), "error"); return; }
    const url = URL.createObjectURL(file);
    S.media.url = url; S.media.file = file;

    const p = window.__createTimbricaPlayer(el.seVideoSlot, {
        trim: false, speed: true, volume: true, fullscreen: true, fsPreferWrap: true,
        skipSeconds: 5, maxHeight: 260,
        onReady: () => {
            S.media.duration = Math.round((p.video.duration || 0) * 1000);
            if (S.tl) S.tl.setDuration(Math.max(S.media.duration, M.totalDuration(S.cues)));
            mountOverlay(p);
        },
        onTimeUpdate: (sec) => onPlayhead(sec)
    });
    p.load(url);
    S.media.player = p;
    el.seApp.classList.add("has-media");
    show(el.seVideoWrap, true);
    show(el.seMediaBar, true);
    if (el.seMediaName) el.seMediaName.textContent = file.name;
    show(el.seLoadMedia, false);
    ensureTimeline();
    ae("feature_use", { f: "media_loaded", type: (file.type || "").split("/")[0] || "?" });
    stage("media");
    loadPeaks(file);
}

function mountOverlay(p) {
    if (!window.SubtitleOverlay || !p.container) return;
    try {
        S.media.overlay = window.SubtitleOverlay.create(p.container, p.video);
        S.media.overlay.loadCues(overlayCues());
        S.media.overlay.setVisible(true);
    } catch (_) { /* the frame still plays; the list is still the source of truth */ }
}

let lastHeadMs = -1;
function onPlayhead(sec) {
    const ms = Math.round(sec * 1000);
    if (ms === lastHeadMs) return;
    lastHeadMs = ms;
    if (S.tl) S.tl.setPlayhead(ms, S.follow);
    if (!S.follow) return;
    const i = M.cueAt(S.cues, ms);
    if (i >= 0 && i !== S.focus) { S.focus = i; S.sel = new Set([i]); paintRows(true); renderInspector(); revealRow(i); if (S.tl) S.tl.setSelection(S.sel); }
}

/**
 * The envelope, without ever holding the file in memory.
 *
 * AudioPeaksStream reads mp4/mov in 4 MB slices and decodes one frame at a
 * time, so a three-hour recording costs a few megabytes of peaks rather than
 * gigabytes of PCM. Anything it cannot open simply gets no waveform — the cue
 * blocks, the playhead and the ruler all still work, so the strip is never
 * empty, and we do NOT pull a 30 MB FFmpeg build onto the page for a picture.
 */
async function loadPeaks(file) {
    if (!window.AudioPeaksStream || !window.AudioPeaksStream.supported(file)) {
        setTlState(t("tl_nowave"));
        ae("feature_use", { f: "peaks_skip", why: "unsupported" });
        return;
    }
    setTlState(t("tl_reading"));
    try {
        const res = await window.AudioPeaksStream.peaks(file, {
            rate: 500,
            onProgress: (f) => setTlState(t("tl_reading") + " " + Math.round(f * 100) + "%")
        });
        if (res && res.samples && res.samples.length && S.tl) {
            S.tl.setPeaks(res.samples, res.sampleRate);
            setTlState("");
            ae("feature_use", { f: "peaks_ok", dur: Math.round(res.duration) });
            return;
        }
        setTlState(t("tl_nowave"));
    } catch (e) {
        setTlState(t("tl_nowave"));
        ae("feature_use", { f: "peaks_failed", msg: String((e && e.message) || e).slice(0, 80) });
    }
}

function setTlState(s) { if (el.seTlState) el.seTlState.textContent = s || ""; }

function stopMedia() {
    if (S.media.overlay) { try { S.media.overlay.destroy(); } catch (_) {} S.media.overlay = null; }
    if (S.media.player) { try { S.media.player.destroy(); } catch (_) {} S.media.player = null; }
    /* The object URL is revoked here and nowhere else: the previous version
     * never revoked it at all, so every media swap leaked the whole file. */
    if (S.media.url) { try { URL.revokeObjectURL(S.media.url); } catch (_) {} S.media.url = null; }
    S.media.file = null; S.media.duration = 0;
    if (S.tl) { S.tl.setPeaks(null); S.tl.setDuration(M.totalDuration(S.cues)); }
    if (el.seApp) el.seApp.classList.remove("has-media");
    show(el.seVideoWrap, false);
    show(el.seMediaBar, false);
    show(el.seLoadMedia, true);
    setTlState("");
}

window.addEventListener("pagehide", stopMedia);

/* ── export ────────────────────────────────────────────────────────────── */

function exportName() {
    const base = outputName(S.fileName, S.target);
    if (!window.__outputName) return base;
    try { return window.__outputName.sanitize(base); } catch (_) { return base; }
}

function wireExport() {
    on(el.seFormat, "change", () => {
        S.target = el.seFormat.value;
        updateChrome();
        window.__toolSettings && window.__toolSettings.save(SLUG, "format", S.target);
        ae("feature_use", { f: "format_pick", v: S.target });
    });
    on(el.seEncOut, "change", () => { S.outEncoding = el.seEncOut.value; });
    on(el.seDownload, "click", doDownload);
    if (window.__toolSettings) {
        const saved = window.__toolSettings.load(SLUG, "format", null);
        if (saved && el.seFormat && Array.prototype.some.call(el.seFormat.options, (o) => o.value === saved)) {
            el.seFormat.value = saved; S.target = saved;
        }
    }
}

function doDownload() {
    if (!S.cues.length) return;
    /* A time field being edited must land in the document before it is written
     * out — otherwise the file is one edit behind what is on screen. */
    const a = document.activeElement;
    if (a && (a === el.seInsStart || a === el.seInsEnd || a === el.seInsText)) a.blur();

    const run = window.__toolLimit ? window.__toolLimit.begin(SLUG, S.limitKey || undefined) : { commit() {}, abort() {} };
    if (!run) return;                              /* the wall is up; it said so */

    try {
        const res = convert({
            name: S.fileName, cues: S.cues, fps: S.fps,
            meta: (S.doc && S.doc.meta) || {}, bytes: null
        }, {
            to: S.target,
            fps: S.fps,
            encoding: S.outEncoding,
            /* The editor is the place where a human decided what the cues are.
             * Running the converter's repair pass over them here would undo
             * deliberate work (a two-speaker overlap, a one-frame flash), so the
             * export writes exactly what is on screen. */
            repair: false
        });
        const name = exportName();
        const blob = new Blob([res.bytes], { type: (formatMime(S.target) || "text/plain") + ";charset=" + S.outEncoding });
        const okDownload = window.__downloadFile
            ? window.__downloadFile(blob, name)
            : (() => { throw new Error("no download primitive"); })();
        run.commit();
        S.dirty = false;
        syncGuard();
        clearSave();
        if (res.lost && res.lost.length) {
            window.__toast && window.__toast(t("lost", { what: res.lost.join(", ") }), "warning");
        }
        ae("tool_complete", {
            format: S.target, cues: S.cues.length, enc: S.outEncoding,
            media: S.media.player ? 1 : 0, issues: S.quality ? S.quality.issues.length : 0,
            delivered: okDownload === false ? 0 : 1
        });
        stage("exported");
    } catch (e) {
        run.abort();
        window.__handleError ? window.__handleError(e, SLUG, { say: t("err_export") }) : console.error(e);
    }
}

function formatMime(id) {
    for (const f of writableFormats()) if (f.id === id) return f.mime;
    return "text/plain";
}

/* ── autosave: an editor must survive the tab ──────────────────────────── */

const SAVE_KEY = "se:draft:v1";
let saveTimer = 0;

function scheduleSave() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(writeSave, 1200);
}

function writeSave() {
    if (!S.cues.length) return;
    try {
        /* Cap what is kept: a 6 000-cue script is ~900 KB of JSON and
         * localStorage refuses somewhere above five megabytes on most engines.
         * Refusing to save is fine; throwing on every keystroke is not. */
        const payload = JSON.stringify({
            at: Date.now(), name: S.fileName, target: S.target, fps: S.fps,
            cues: S.cues.map((c) => [c.start, c.end, c.text])
        });
        if (payload.length > 3_000_000) return;
        localStorage.setItem(SAVE_KEY, payload);
    } catch (_) { /* private mode, quota, blocked storage — all survivable */ }
}

function clearSave() { try { localStorage.removeItem(SAVE_KEY); } catch (_) {} }

function offerRestore() {
    let raw = null;
    try { raw = localStorage.getItem(SAVE_KEY); } catch (_) { return; }
    if (!raw) return;
    let saved;
    try { saved = JSON.parse(raw); } catch (_) { clearSave(); return; }
    if (!saved || !saved.cues || !saved.cues.length) { clearSave(); return; }
    /* A week-old draft is not an offer, it is clutter. */
    if (Date.now() - (saved.at || 0) > 7 * 86400_000) { clearSave(); return; }

    show(el.seRestore, true);
    const when = new Date(saved.at || Date.now());
    const label = el.seRestore.querySelector("[data-restore-what]");
    if (label) label.textContent = t("restore_what", {
        name: saved.name || "subtitles",
        n: saved.cues.length,
        when: when.toLocaleString()
    });
    ae("feature_use", { f: "draft_offered", age_h: Math.round((Date.now() - saved.at) / 3600_000) });

    on(el.seRestoreYes, "click", () => {
        const cues = saved.cues.map((a) => M.cue(a[0], a[1], a[2]));
        adopt({ name: saved.name || "subtitles.srt", cues, fps: saved.fps || 25, meta: {}, encoding: "utf-8", format: saved.target || "srt" }, cues, "draft");
        S.target = saved.target || "srt";
        if (el.seFormat) el.seFormat.value = S.target;
        show(el.seRestore, false);
        ae("feature_use", { f: "draft_restored", cues: cues.length });
    });
    on(el.seRestoreNo, "click", () => { clearSave(); show(el.seRestore, false); });
}

/* ── keyboard: this is where the work actually happens ─────────────────── */

function inField(e) {
    const n = e.target;
    return n && (n.tagName === "INPUT" || n.tagName === "TEXTAREA" || n.isContentEditable);
}

function wireKeys() {
    document.addEventListener("keydown", (e) => {
        if (!S.doc) return;
        const mod = e.ctrlKey || e.metaKey;

        if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); stepHistory(e.shiftKey ? 1 : -1); return; }
        if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); stepHistory(1); return; }
        if (mod && e.key.toLowerCase() === "f") {
            e.preventDefault(); show(el.seFind, true); el.seFindIn.focus({ preventScroll: true }); runFind(); return;
        }
        if (mod && e.key.toLowerCase() === "s") { e.preventDefault(); doDownload(); return; }

        if (e.key === "Escape") {
            if (!el.seFind.hidden) { show(el.seFind, false); clearFindMarks(); return; }
            if (document.activeElement === el.seInsText) { el.seInsText.blur(); el.seList.focus({ preventScroll: true }); return; }
        }

        /* Everything below is a bare key, so it must not fire while typing. */
        if (inField(e)) {
            if (e.key === "Enter" && !e.shiftKey && document.activeElement === el.seInsText && mod) {
                e.preventDefault(); doSplit();
            }
            return;
        }

        switch (e.key) {
            case "ArrowDown": case "j":
                e.preventDefault(); focusCue(Math.min(S.cues.length - 1, S.focus + 1), S.follow === false); break;
            case "ArrowUp": case "k":
                e.preventDefault(); focusCue(Math.max(0, S.focus - 1), S.follow === false); break;
            case " ":
                if (S.media.player) { e.preventDefault(); S.media.player.togglePlay(); }
                break;
            case "Enter":
                e.preventDefault(); el.seInsText && el.seInsText.focus({ preventScroll: true }); break;
            case "Delete": case "Backspace":
                if (S.sel.size) { e.preventDefault(); el.seDelete.click(); }
                break;
            case "[": e.preventDefault(); setEdgeAtPlayhead("start"); break;
            case "]": e.preventDefault(); setEdgeAtPlayhead("end"); break;
            case "m": case "M": if (S.sel.size > 1) { e.preventDefault(); el.seMerge.click(); } break;
            case "n": case "N": e.preventDefault(); el.seAdd.click(); break;
        }
    });

}

/* ── "you have unsaved edits" ──────────────────────────────────────────────
 * Through the shared primitive and never with an own beforeunload listener: a
 * permanent one disables the back/forward cache for the WHOLE SITE, so one
 * tool's safety net would slow every navigation on every page. The primitive
 * attaches the listener only while armed, and only our own reason is ever
 * disarmed so a neighbouring guard is not switched off. */
const GUARD_REASON = "edit:" + SLUG;
let guardArmed = false;
function syncGuard() {
    const U = window.__unsavedWork;
    if (!U) return;
    const want = S.dirty && S.cues.length > 0;
    if (want === guardArmed) return;
    guardArmed = want;
    if (want) U.arm(GUARD_REASON);
    else if (U.isArmed() && U.reason() === GUARD_REASON) U.disarm();
}

/* ── go ────────────────────────────────────────────────────────────────── */
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();
